'use client'

import { useEffect, useRef, useState } from 'react'
import Link from 'next/link'
import { Playfair_Display } from 'next/font/google'
import { RESTRO_REGISTER_URL } from '@/config/portal'
import './CinematicHomeBanner.css'

const playfair = Playfair_Display({
  subsets: ['latin', 'latin-ext'],
  weight: ['500', '600'],
  display: 'swap',
})

type TFn = (key: string) => string

type Props = {
  t: TFn
  lp: string
}

/** Previous hero: waiter / rush drinks (Pexels #3981581, local copy) */
const HERO_MP4 = '/videos/hero/rush-service-drinks.mp4'
const HERO_POSTER = '/videos/hero/rush-service-drinks.jpg'

/** Full-bleed video hero + Farori offer bar (from airestro-homepage-video-sections_2). */
export default function CinematicHomeBanner({ t, lp }: Props) {
  const videoRef = useRef<HTMLVideoElement | null>(null)
  const [playing, setPlaying] = useState(true)

  useEffect(() => {
    const el = videoRef.current
    if (!el) return
    el.muted = true
    el.play().catch(() => setPlaying(false))
  }, [])

  const togglePlay = () => {
    const el = videoRef.current
    if (!el) return
    if (playing) {
      el.pause()
      setPlaying(false)
    } else {
      el.play().catch(() => {})
      setPlaying(true)
    }
  }

  return (
    <div className="ar-cine-wrap">
      {/* Farori offer bar lives in SiteLayoutClient header on home (stacked under nav). */}
      <section
        className={`ar-cine${playing ? '' : ' ar-cine--paused'}`}
        aria-labelledby="ar-cine-title"
      >
        <video
          ref={videoRef}
          className="ar-cine__video"
          autoPlay
          muted
          loop
          playsInline
          preload="auto"
          poster={HERO_POSTER}
          aria-hidden
        >
          <source src={HERO_MP4} type="video/mp4" />
        </video>
        <div className="ar-cine__overlay" aria-hidden />

        <button
          type="button"
          className="ar-cine__toggle"
          onClick={togglePlay}
          aria-label={playing ? t('landing.cinePauseVideo') : t('landing.cinePlayVideo')}
        >
          {playing ? (
            <svg viewBox="0 0 24 24" aria-hidden>
              <rect x="6" y="5" width="4" height="14" />
              <rect x="14" y="5" width="4" height="14" />
            </svg>
          ) : (
            <svg viewBox="0 0 24 24" aria-hidden>
              <polygon points="7,5 19,12 7,19" />
            </svg>
          )}
        </button>

        <div className="ar-cine__content">
          <h1 id="ar-cine-title" className={`ar-cine__title ${playfair.className}`}>
            {t('landing.cineTitle')}
          </h1>
          <p className="ar-cine__sub">{t('landing.cineSubtitle')}</p>
          <div className="ar-cine__ctas">
            <a
              className="ar-cine__cta ar-cine__cta--filled"
              href={RESTRO_REGISTER_URL}
              target="_blank"
              rel="noopener noreferrer"
            >
              {t('landing.cineCtaTrial')}
            </a>
            <Link className="ar-cine__cta" href={`${lp}/contact`}>
              {t('landing.cineCtaDemo')}
            </Link>
          </div>
        </div>
      </section>
    </div>
  )
}
